import * as React from 'react';
import { TextField, IconButton, Dialog, DialogContent, DialogTitle, Tooltip } from '@mui/material';
import { renderHelpText } from './renderHelpText';
import Add from '@material-ui/icons/Add';
import DragIndicator from '@material-ui/icons/DragIndicator';
import FormatListBulleted from '@material-ui/icons/FormatListBulleted';
import HelpIcon from '@material-ui/icons/Help';
import Notes from '@material-ui/icons/Notes';
import Remove from '@material-ui/icons/Remove';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';

export type InputType = 'text' | 'password' | 'email' | 'number' | 'tel' | 'url';

export interface MultipleTextFieldProps {
    id?: string,
    label?: string,
    helpText?: string,
    description?: string,
    values: string[],
    type?: InputType,
    disabled?: boolean,
    onChange: (value: string[]) => void,
}

export const MultipleTextField = (props: MultipleTextFieldProps) => {
    const {
        id,
        label,
        helpText,
        description,
        type,
        values,
        onChange,
        disabled,
    } = props;

    const t = useTranslations();

    const [showHelp, setShowHelp] = React.useState(false);
    const [textMode, setTextMode] = React.useState(false);
    const [textDraft, setTextDraft] = React.useState('');
    const dragIndex = React.useRef<number | null>(null);

    const commitTextDraft = (draft: string) => {
        onChange(draft.split('|').map(s => s.trim()).filter(s => s.length > 0));
    };

    const toggleTextMode = () => {
        if (!textMode) setTextDraft(values.join(' | '));
        else commitTextDraft(textDraft);
        setTextMode(v => !v);
    };

    const onNumberChanged = (number: string, idx: number) => {
        onChange(values.map((v, i) => i === idx ? number : v));
    }

    const handleDragStart = (idx: number) => {
        dragIndex.current = idx;
    };

    const handleDrop = (idx: number) => {
        if (dragIndex.current === null || dragIndex.current === idx) return;
        const reordered = [...values];
        const [moved] = reordered.splice(dragIndex.current, 1);
        reordered.splice(idx, 0, moved);
        onChange(reordered);
        dragIndex.current = null;
    };

    return (
        <div className="multipleInputsContainer">
            {label &&
                <label style={{ fontSize: '0.875rem', marginBottom: '8px', display: 'block', fontWeight: 500 }}>
                    {helpText
                        ? <div>{label} <IconButton size="small" onClick={() => setShowHelp(true)}><HelpIcon/></IconButton></div>
                        : label}
                </label>}
            {textMode
                ? <TextField
                    multiline
                    minRows={3}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={textDraft}
                    onChange={e => setTextDraft(e.target.value)}
                    onBlur={() => commitTextDraft(textDraft)}
                    helperText={description}
                  />
                : values.map((phone, idx) => (
                    <div
                        key={idx}
                        className="multipleInputItem"
                        draggable={!disabled}
                        onDragStart={() => handleDragStart(idx)}
                        onDragOver={e => e.preventDefault()}
                        onDrop={() => handleDrop(idx)}
                    >
                        { !disabled && <DragIndicator style={{ cursor: 'grab', color: 'var(--appLigthGrey)', flexShrink: 0 }} /> }
                        <TextField
                            id={id ? `${id}-${idx}-input` : undefined}
                            disabled={disabled}
                            type={type || 'text'}
                            helperText={description}
                            value={phone || ''}
                            onChange={e => onNumberChanged(e.target.value, idx)}
                            variant="outlined"
                            size="small"
                            fullWidth
                        />
                        { !disabled &&
                            <IconButton
                                id={id ? `${id}-${idx}-remove-button` : undefined}
                                onClick={() => onChange(values.filter((_, i) => i !== idx))}
                                size="small"
                            >
                                <Remove/>
                            </IconButton>
                        }
                    </div>
                ))
            }
            { !disabled && (
                <div style={{justifyContent: 'end', width: '100%'}}>
                    <Tooltip title={textMode ? t(TK.switchToListMode) : t(TK.switchToTextMode)}>
                        <IconButton
                            style={{margin: 'var(--spacer-XS)'}}
                            onClick={toggleTextMode}
                            size="small"
                        >
                            {textMode ? <FormatListBulleted/> : <Notes/>}
                        </IconButton>
                    </Tooltip>
                    {!textMode &&
                        <Tooltip title={t(TK.addItem)}>
                            <IconButton
                                style={{margin: 'var(--spacer-XS)'}}
                                id={id ? `${id}-add-button` : undefined}
                                onClick={() => onChange(values.concat(''))}
                                size="small"
                            >
                                <Add/>
                            </IconButton>
                        </Tooltip>
                    }
                </div>
            )}

            {(helpText?.length &&
                <Dialog open={showHelp} onClose={() => setShowHelp(false)}>
                    <DialogTitle>{label}</DialogTitle>
                    <DialogContent>{renderHelpText(helpText)}</DialogContent>
                </Dialog>
            ) || undefined}
        </div>
    );
}
